import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { SCAFFOLDING_SECTIONS } from "./Scaffolding";
import { createBlankReport, REPORT_FIELDS } from "../data/BOOK_REPORT_STRUCTURE";

// Unique ID generator for quizzes and questions
function generateId() {
  return Math.random().toString(36).substring(2, 10);
}

// A blank question template for each type
function blankQuestion(type) {
  switch (type) {
    case "multiple_choice":
      return { type, question: "", options: ["", ""], correctAnswer: 0 };
    case "true_false":
      return { type, question: "", correctAnswer: 0 };
    case "multi_select":
      return { type, question: "", options: ["", ""], correctAnswers: [] };
    case "dropdown":
      return { type, question: "", options: ["", ""], correctAnswer: 0 };
    case "free_response":
      return { type, question: "" };
    default:
      return { type: "free_response", question: "" };
  }
}

export default function ParentBookView() {
  const { childId, bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [activeTab, setActiveTab] = useState("sessions");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Quiz builder state
  const [showQuizBuilder, setShowQuizBuilder] = useState(false);
  const [quizTitle, setQuizTitle] = useState("");
  const [quizMilestone, setQuizMilestone] = useState("");
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [savingQuiz, setSavingQuiz] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState(null);
  // When non-null, the builder is editing a saved template (by index in matchingTemplates)
  // rather than a quiz on the book. saveQuiz() branches on this.
  const [editingTemplateIdx, setEditingTemplateIdx] = useState(null);
  const builderRef = React.useRef(null);

  // Scoring + feedback state — combined into one save action to avoid the
  // race condition where two separate writes to the same document clobber
  // each other (this was the cause of the "scores erased on save" bug).
  const [pendingScores, setPendingScores] = useState({});
  const [quizFeedback, setQuizFeedback] = useState({});
  const [savingReview, setSavingReview] = useState(false);

  // Report builder state
  const [savingReport, setSavingReport] = useState(false);
  const [reportFeedbackDraft, setReportFeedbackDraft] = useState({});
  const [savingReportFeedback, setSavingReportFeedback] = useState(false);

  // ── PHASE 4: COLLAPSIBLE STATE ──

  // Outline tab — each SCAFFOLDING_SECTIONS id maps to true/false
  const [expandedOutlineSections, setExpandedOutlineSections] = useState({});

  // Quizzes tab — each quiz id maps to true/false
  // When empty (first load), the most recent quiz (highest milestone) defaults open
  const [expandedQuizIds, setExpandedQuizIds] = useState({});

  // Reports tab — each report id maps to true/false
  // When empty (first load), the most recent report defaults open
  const [expandedReportIds, setExpandedReportIds] = useState({});

  // History tab — localStorage tracks last visit so "New" badges can clear on next open
  const [historyLastViewed, setHistoryLastViewed] = useState(
    () => parseInt(localStorage.getItem(`historyLastViewed_${bookId}`) || "0")
  );
  const [expandedHistorySections, setExpandedHistorySections] = useState({});

  // When the parent opens the History tab, record the timestamp so entries
  // saved after this point will appear as "New" next visit (not this one).
  useEffect(() => {
    if (activeTab === "history") {
      const now = Date.now();
      localStorage.setItem(`historyLastViewed_${bookId}`, String(now));
      setHistoryLastViewed(now);
    }
  }, [activeTab, bookId]);

  // ── PHASE 5: QUIZ TEMPLATES ──

  const { currentUser } = useAuth();

  // Parent's saved templates — keyed by normalized "title|author" string
  const [savedTemplates, setSavedTemplates] = useState({});
  // Editable copies of templates the parent is about to apply to this book
  const [templateDraft, setTemplateDraft] = useState([]);
  const [showTemplateApply, setShowTemplateApply] = useState(false);
  // Dismissed per book so the prompt doesn't re-appear once the parent acts
  const [templatesDismissed, setTemplatesDismissed] = useState(
    () => localStorage.getItem(`templatesDismissed_${bookId}`) === "true"
  );

  // Normalizes title + author into a stable lookup key.
  // Same book assigned to two different children will produce the same key
  // so the parent is prompted to reuse their existing quizzes.
  function makeBookKey(title, author) {
    return `${(title || "").toLowerCase().trim()}|${(author || "").toLowerCase().trim()}`;
  }

  // Load this parent's saved quiz templates once on mount
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, "users", currentUser.uid)).then((snap) => {
      if (snap.exists()) setSavedTemplates(snap.data().quizTemplates || {});
    }).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", bookId), (snap) => {
      if (snap.exists()) setBook({ id: snap.id, ...snap.data() });
    });
    return unsubscribe;
  }, [bookId]);

  async function deleteBook() {
    try {
      await deleteDoc(doc(db, "books", bookId));
      navigate(`/parent/child/${childId}`);
    } catch {
      alert("Failed to delete book. Try again.");
    }
  }

  // ── QUIZ BUILDER HELPERS ──

  function addQuestion(type) {
    setQuizQuestions((prev) => [...prev, blankQuestion(type)]);
  }

  function updateQuestion(index, field, value) {
    setQuizQuestions((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }

  function updateOption(qIndex, oIndex, value) {
    setQuizQuestions((prev) => {
      const updated = [...prev];
      const opts = [...(updated[qIndex].options || [])];
      opts[oIndex] = value;
      updated[qIndex] = { ...updated[qIndex], options: opts };
      return updated;
    });
  }

  function addOption(qIndex) {
    setQuizQuestions((prev) => {
      const updated = [...prev];
      const opts = [...(updated[qIndex].options || []), ""];
      updated[qIndex] = { ...updated[qIndex], options: opts };
      return updated;
    });
  }

  function removeOption(qIndex, oIndex) {
    setQuizQuestions((prev) => {
      const updated = [...prev];
      const opts = updated[qIndex].options.filter((_, i) => i !== oIndex);
      updated[qIndex] = { ...updated[qIndex], options: opts };
      return updated;
    });
  }

  function toggleCorrectAnswer(qIndex, oIndex) {
    setQuizQuestions((prev) => {
      const updated = [...prev];
      const q = updated[qIndex];
      if (q.type === "multi_select") {
        const current = q.correctAnswers || [];
        const next = current.includes(oIndex)
          ? current.filter((i) => i !== oIndex)
          : [...current, oIndex];
        updated[qIndex] = { ...q, correctAnswers: next };
      } else {
        updated[qIndex] = { ...q, correctAnswer: oIndex };
      }
      return updated;
    });
  }

  function removeQuestion(index) {
    setQuizQuestions((prev) => prev.filter((_, i) => i !== index));
  }

  function startEditingQuiz(quiz) {
    setEditingQuizId(quiz.id);
    setEditingTemplateIdx(null);
    setQuizTitle(quiz.title);
    setQuizMilestone(String(quiz.milestone));
    setQuizQuestions((quiz.questions || []).map(q => ({ ...q })));
    setShowQuizBuilder(true);
    setTimeout(() => {
      builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  // Opens the quiz builder pre-filled with a saved template so the parent
  // can change title, milestone, or questions before saving back.
  function startEditingTemplate(idx) {
    const t = matchingTemplates[idx];
    setEditingTemplateIdx(idx);
    setEditingQuizId(null);
    setQuizTitle(t.title);
    setQuizMilestone(String(t.milestone));
    setQuizQuestions((t.questions || []).map(q => ({ ...q })));
    setShowQuizBuilder(true);
    setTimeout(() => {
      builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }

  async function deleteTemplate(idx) {
    if (!window.confirm("Delete this template? This can't be undone.")) return;
    const updatedForBook = matchingTemplates.filter((_, i) => i !== idx);
    const updatedTemplates = { ...savedTemplates };
    if (updatedForBook.length === 0) {
      delete updatedTemplates[bookKey];
    } else {
      updatedTemplates[bookKey] = updatedForBook;
    }
    try {
      await updateDoc(doc(db, "users", currentUser.uid), { quizTemplates: updatedTemplates });
      setSavedTemplates(updatedTemplates);
    } catch {
      alert("Failed to delete template. Try again.");
    }
  }

  async function saveQuiz() {
    if (!quizTitle.trim()) return alert("Please enter a quiz title.");
    if (!quizMilestone || isNaN(parseInt(quizMilestone))) return alert("Please enter a valid milestone page number.");
    if (quizQuestions.length === 0) return alert("Please add at least one question.");

    const milestone = parseInt(quizMilestone);
    if (milestone < 1 || milestone > book.totalPages) {
      return alert(`Milestone must be between 1 and ${book.totalPages}.`);
    }

    // Validate each question
    for (let i = 0; i < quizQuestions.length; i++) {
      const q = quizQuestions[i];
      if (!q.question.trim()) return alert(`Question ${i + 1} needs a question prompt.`);
      if (q.type !== "free_response" && q.type !== "true_false") {
        if (!q.options || q.options.some((o) => !o.trim())) {
          return alert(`Question ${i + 1} has empty options. Please fill them all in.`);
        }
      }
      if (q.type === "multi_select" && (!q.correctAnswers || q.correctAnswers.length === 0)) {
        return alert(`Question ${i + 1} needs at least one correct answer selected.`);
      }
    }

    setSavingQuiz(true);

    // ── Template edit path ──
    // When editingTemplateIdx is set we write back to the parent's user doc,
    // not to the book's quizzes array.
    if (editingTemplateIdx !== null) {
      try {
        const updatedForBook = matchingTemplates.map((t, i) =>
          i === editingTemplateIdx
            ? { title: quizTitle.trim(), milestone, questions: quizQuestions }
            : t
        );
        const updatedTemplates = { ...savedTemplates, [bookKey]: updatedForBook };
        await updateDoc(doc(db, "users", currentUser.uid), { quizTemplates: updatedTemplates });
        setSavedTemplates(updatedTemplates);
        setQuizTitle(""); setQuizMilestone(""); setQuizQuestions([]);
        setShowQuizBuilder(false); setEditingTemplateIdx(null);
        alert("Template updated!");
      } catch {
        alert("Failed to update template. Try again.");
      }
      setSavingQuiz(false);
      return;
    }

    // ── Quiz edit / create path ──
    try {
      const existingQuizzes = book.quizzes || [];

      let updatedQuizzes;
      if (editingQuizId) {
        // Update existing quiz — preserve completion state
        updatedQuizzes = existingQuizzes.map((q) =>
          q.id === editingQuizId
            ? { ...q, title: quizTitle.trim(), milestone, questions: quizQuestions }
            : q
        );
      } else {
        // Create new quiz
        const newQuiz = {
          id: generateId(),
          title: quizTitle.trim(),
          milestone,
          unlockedByParent: false,
          completedByChild: false,
          questions: quizQuestions,
          childAnswers: {},
          parentScores: {},
        };
        updatedQuizzes = [...existingQuizzes, newQuiz];
      }

      await updateDoc(doc(db, "books", bookId), { quizzes: updatedQuizzes });

      // Reset builder
      setQuizTitle("");
      setQuizMilestone("");
      setQuizQuestions([]);
      setShowQuizBuilder(false);
      setEditingQuizId(null);
      alert(editingQuizId ? "Quiz updated!" : "Quiz saved!");
    } catch {
      alert("Failed to save quiz. Try again.");
    }
    setSavingQuiz(false);
  }

  // ── UNLOCK CONFIRMATION ──

  // Function 1 — creates a blank report draft and navigates parent to it
  async function createNewReport() {
    const existingReports = book.bookReports || [];
    const draftNumber = existingReports.length + 1;
    const newReport = createBlankReport(`Draft ${draftNumber}`);
    const updatedReports = [...existingReports, newReport];
    setSavingReport(true);
    try {
      await updateDoc(doc(db, "books", bookId), { bookReports: updatedReports });
      navigate(`/book/${bookId}/report/${newReport.id}`);
    } catch {
      alert("Failed to create report. Try again.");
    }
    setSavingReport(false);
  }

  // Function 2 — sends a submitted report back to the child for revision
  async function kickBackReport(reportId) {
    const updatedReports = (book.bookReports || []).map((r) =>
      r.id === reportId ? { ...r, status: "kickedBack" } : r
    );
    try {
      await updateDoc(doc(db, "books", bookId), { bookReports: updatedReports });
    } catch {
      alert("Failed to kick back report. Try again.");
    }
  }

  // Function 3 — saves parent feedback on a specific field of a report
  function updateReportFeedbackDraft(reportId, fieldId, text) {
    setReportFeedbackDraft((prev) => ({
      ...prev,
      [reportId]: { ...(prev[reportId] || {}), [fieldId]: text },
    }));
  }

  async function saveAllReportFeedback(reportId) {
    const feedback = reportFeedbackDraft[reportId] || {};
    const updatedReports = (book.bookReports || []).map((r) => {
      if (r.id !== reportId) return r;
      const updatedFields = { ...r.fields };
      Object.entries(feedback).forEach(([fieldId, text]) => {
        updatedFields[fieldId] = { ...updatedFields[fieldId], parentFeedback: text };
      });
      return { ...r, fields: updatedFields };
    });
    setSavingReportFeedback(true);
    try {
      await updateDoc(doc(db, "books", bookId), { bookReports: updatedReports });
      alert("Feedback saved!");
    } catch {
      alert("Failed to save feedback. Try again.");
    }
    setSavingReportFeedback(false);
  }

  // Updates local feedback state as parent types — nothing saved to Firestore yet
  function updateQuizFeedback(quizId, questionIndex, text) {
    setQuizFeedback((prev) => ({
      ...prev,
      [quizId]: { ...(prev[quizId] || {}), [questionIndex]: text },
    }));
  }

  // Saves BOTH free-response scores AND per-question feedback in a single
  // Firestore write. Combining these into one updateDoc call is critical —
  // two separate writes built from the same stale `book` snapshot would race
  // each other, and whichever lands last wins, silently erasing the other's
  // changes.
  async function saveReviewAndFeedback(quizId) {
    const scores = pendingScores[quizId] || {};
    const feedback = quizFeedback[quizId] || {};

    const updatedQuizzes = (book.quizzes || []).map((q) => {
      if (q.id !== quizId) return q;

      const updatedQuestions = (q.questions || []).map((question, i) => ({
        ...question,
        parentFeedback: feedback[i] !== undefined
          ? feedback[i]
          : (question.parentFeedback || ""),
      }));

      return {
        ...q,
        parentScores: { ...(q.parentScores || {}), ...scores },
        questions: updatedQuestions,
      };
    });

    setSavingReview(true);
    try {
      await updateDoc(doc(db, "books", bookId), { quizzes: updatedQuizzes });
      setPendingScores((prev) => ({ ...prev, [quizId]: {} }));
      alert("Scores and feedback saved!");
    } catch {
      alert("Failed to save. Try again.");
    }
    setSavingReview(false);
  }

  // FORK 1 — Finalize: saves scores+feedback AND marks the review complete.
  // The student will see their results and feedback as final. This is the
  // function that was missing entirely — without it, reviewedByParent could
  // never become true, and the student-side "Final Score" / feedback view in
  // Quiz.js (which checks quiz.reviewedByParent) had no way to ever trigger.
  async function finalizeReview(quizId) {
    const scores = pendingScores[quizId] || {};
    const feedback = quizFeedback[quizId] || {};

    const updatedQuizzes = (book.quizzes || []).map((q) => {
      if (q.id !== quizId) return q;

      const updatedQuestions = (q.questions || []).map((question, i) => ({
        ...question,
        parentFeedback: feedback[i] !== undefined
          ? feedback[i]
          : (question.parentFeedback || ""),
      }));

      return {
        ...q,
        parentScores: { ...(q.parentScores || {}), ...scores },
        questions: updatedQuestions,
        reviewedByParent: true,
        needsRevision: false,
        // Reset so the student sees this as new feedback — Quiz.js will flip
        // it to true the moment they open the quiz.
        feedbackSeenByChild: false,
      };
    });

    setSavingReview(true);
    try {
      await updateDoc(doc(db, "books", bookId), { quizzes: updatedQuizzes });
      setPendingScores((prev) => ({ ...prev, [quizId]: {} }));
      alert("Review finalized! Your child can now see their results and feedback.");
    } catch {
      alert("Failed to finalize review. Try again.");
    }
    setSavingReview(false);
  }

  // FORK 2 — Send Back: saves scores+feedback AND flags the quiz for revision.
  // Now also bundles the scores/feedback save into the same write (previously
  // this only set needsRevision and any unsaved scores/feedback in the form
  // would be lost unless the parent saved first as a separate click).
  async function kickBackQuiz(quizId) {
    const scores = pendingScores[quizId] || {};
    const feedback = quizFeedback[quizId] || {};

    const updatedQuizzes = (book.quizzes || []).map((q) => {
      if (q.id !== quizId) return q;

      const updatedQuestions = (q.questions || []).map((question, i) => ({
        ...question,
        parentFeedback: feedback[i] !== undefined
          ? feedback[i]
          : (question.parentFeedback || ""),
      }));

      return {
        ...q,
        parentScores: { ...(q.parentScores || {}), ...scores },
        questions: updatedQuestions,
        needsRevision: true,
        reviewedByParent: false,
      };
    });

    setSavingReview(true);
    try {
      await updateDoc(doc(db, "books", bookId), { quizzes: updatedQuizzes });
      setPendingScores((prev) => ({ ...prev, [quizId]: {} }));
      alert("Quiz sent back to child for revision.");
    } catch {
      alert("Failed to kick back quiz. Try again.");
    }
    setSavingReview(false);
  }

  async function confirmUnlock(quizId) {
    const updatedQuizzes = (book.quizzes || []).map((q) =>
      q.id === quizId ? { ...q, unlockedByParent: true } : q
    );
    try {
      await updateDoc(doc(db, "books", bookId), { quizzes: updatedQuizzes });
    } catch {
      alert("Failed to unlock quiz. Try again.");
    }
  }

  // Strips all runtime fields from a quiz and saves the clean definition
  // under the parent's user doc, keyed by normalized title|author.
  // If a template with the same title already exists it is overwritten.
  async function saveAsTemplate(quiz) {
    if (!currentUser || !book) return;
    const key = makeBookKey(book.title, book.author);
    const templateDef = {
      title: quiz.title,
      milestone: quiz.milestone,
      questions: quiz.questions,
    };
    try {
      const snap = await getDoc(doc(db, "users", currentUser.uid));
      const existing = snap.exists() ? (snap.data().quizTemplates || {}) : {};
      const existingForBook = existing[key] || [];
      const alreadyExists = existingForBook.some((t) => t.title === quiz.title);
      const updatedForBook = alreadyExists
        ? existingForBook.map((t) => t.title === quiz.title ? templateDef : t)
        : [...existingForBook, templateDef];
      const updatedTemplates = { ...existing, [key]: updatedForBook };
      await updateDoc(doc(db, "users", currentUser.uid), {
        quizTemplates: updatedTemplates,
      });
      setSavedTemplates(updatedTemplates);
      alert(`"${quiz.title}" saved as a template!`);
    } catch {
      alert("Failed to save template. Try again.");
    }
  }

  // Applies the parent's edited template drafts as fresh quizzes on this book.
  // All runtime fields are reset so the child starts clean.
  async function applyTemplates() {
    const selected = templateDraft.filter((t) => t.selected);
    if (selected.length === 0) return alert("Select at least one quiz to apply.");
    const milestoneNum = (t) => parseInt(t.draftMilestone);
    const invalid = selected.find(
      (t) => !t.draftTitle.trim() || isNaN(milestoneNum(t)) ||
             milestoneNum(t) < 1 || milestoneNum(t) > book.totalPages
    );
    if (invalid) return alert(`Check the title and page number for "${invalid.draftTitle || "one of the quizzes"}".`);

    const newQuizzes = selected.map((t) => ({
      id: generateId(),
      title: t.draftTitle.trim(),
      milestone: milestoneNum(t),
      questions: t.questions,
      // Runtime fields reset to blank — child starts fresh
      unlockedByParent: false,
      completedByChild: false,
      childAnswers: {},
      parentScores: {},
      needsRevision: false,
      reviewedByParent: false,
      feedbackSeenByChild: false,
    }));

    try {
      await updateDoc(doc(db, "books", bookId), {
        quizzes: [...(book.quizzes || []), ...newQuizzes],
      });
      setShowTemplateApply(false);
      localStorage.setItem(`templatesDismissed_${bookId}`, "true");
      setTemplatesDismissed(true);
      alert(`${newQuizzes.length} quiz${newQuizzes.length > 1 ? "zes" : ""} applied!`);
    } catch {
      alert("Failed to apply templates. Try again.");
    }
  }

  function dismissTemplates() {
    localStorage.setItem(`templatesDismissed_${bookId}`, "true");
    setTemplatesDismissed(true);
  }

  // ── FREE RESPONSE SCORING ──

  function setPendingScore(quizId, questionIndex, value) {
    setPendingScores((prev) => ({
      ...prev,
      [quizId]: { ...(prev[quizId] || {}), [questionIndex]: parseInt(value) }
    }));
  }

  // ── SCORE CALCULATOR ──

  function calcQuizScore(quiz) {
    let earned = 0;
    let possible = 0;
    (quiz.questions || []).forEach((q, i) => {
      const ans = (quiz.childAnswers || {})[i];
      if (q.type === "free_response") {
        possible += 10;
        if (quiz.parentScores?.[i] !== undefined) earned += quiz.parentScores[i];
      } else if (q.type === "multi_select") {
        const correct = q.correctAnswers || [];
        possible += correct.length;
        (ans || []).forEach((ci) => { if (correct.includes(ci)) earned++; });
      } else {
        possible += 1;
        if (ans === q.correctAnswer) earned++;
      }
    });
    return { earned, possible };
  }

  if (!book) return <div style={styles.loading}>Loading...</div>;

  const sessions = book.sessions || [];
  const scaffolding = book.scaffolding || {};
  const history = [...(book.scaffoldingHistory || [])].reverse();
  const quizzes = [...(book.quizzes || [])].sort((a, b) => a.milestone - b.milestone);
  const pendingUnlocks = quizzes.filter(
    (q) => !q.unlockedByParent && (book.currentPage || 0) >= q.milestone
  );

  // Template prompt conditions — show banner when there are saved templates
  // for this book and the child has no quizzes yet
  const bookKey = makeBookKey(book.title, book.author);
  const matchingTemplates = savedTemplates[bookKey] || [];
  const showTemplateBanner = matchingTemplates.length > 0 &&
    quizzes.length === 0 &&
    !templatesDismissed;

  function formatDate(isoString) {
    return new Date(isoString).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric"
    });
  }

  function formatDateTime(isoString) {
    return new Date(isoString).toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit"
    });
  }

  function getSectionMeta(sectionId) {
    return SCAFFOLDING_SECTIONS.find((s) => s.id === sectionId);
  }

  const tabs = [
    { id: "sessions", label: "📅 Sessions" },
    { id: "scaffolding", label: "📝 Outline" },
    { id: "history", label: "🕓 History" },
    { id: "quizzes", label: "🎯 Quizzes" },
    { id: "reports", label: "📄 Reports" },
  ];

  const QUESTION_TYPES = [
    { type: "multiple_choice", label: "Multiple Choice" },
    { type: "true_false", label: "True / False" },
    { type: "multi_select", label: "Multi-Select" },
    { type: "dropdown", label: "Dropdown" },
    { type: "free_response", label: "Free Response" },
  ];

  return (
    <div style={styles.container}>
      <Header />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div style={styles.modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Remove this book?</h3>
            <p style={styles.modalText}>
              This will permanently delete <strong>{book.title}</strong> along with all reading sessions and notes. This cannot be undone.
            </p>
            <div style={styles.modalButtons}>
              <button style={styles.modalCancel} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
              <button style={styles.modalConfirm} onClick={deleteBook}>
                🗑️ Remove Book
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div style={styles.pageHeader}>
        <button style={styles.backBtn} onClick={() => navigate(`/parent/child/${childId}`)}>
          ← Back
        </button>
        <div style={styles.headerText}>
          <h2 style={styles.title}>{book.title}</h2>
          <p style={styles.subtitle}>by {book.author}</p>
        </div>
        {book.coverUrl && (
          <img src={book.coverUrl} alt={book.title} style={styles.headerCover} />
        )}
        <button style={styles.closeBtn} onClick={() => setShowDeleteModal(true)} title="Remove book">
          ✕
        </button>
      </div>

      {/* Progress summary bar */}
      <div style={styles.summaryBar}>
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${Math.min(((book.pagesRead || 0) / book.totalPages) * 100, 100)}%`
          }} />
        </div>
        <div style={styles.summaryFooter}>
          <p style={styles.progressText}>
            {book.pagesRead || 0} of {book.totalPages} pages read
            ({Math.round(((book.pagesRead || 0) / book.totalPages) * 100)}%)
          </p>
          <button
            style={styles.editBookBtn}
            onClick={() => navigate(`/book/${bookId}/edit`)}
          >
            ✏️ Edit Details
          </button>
        </div>
      </div>

      {/* Pending unlock alert banner */}
      {pendingUnlocks.length > 0 && (
        <div style={styles.unlockBanner}>
          <p style={styles.unlockBannerText}>
            ⏳ {pendingUnlocks.length} quiz{pendingUnlocks.length > 1 ? "zes" : ""} waiting for your approval — go to the Quizzes tab to unlock.
          </p>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabRow}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            style={activeTab === tab.id ? styles.tabActive : styles.tabInactive}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div style={styles.content}>

        {/* ── SESSIONS TAB ── */}
        {activeTab === "sessions" && (
          <div>
            {sessions.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No reading sessions logged yet.</p>
              </div>
            ) : (
              [...sessions].reverse().map((session, i) => (
                <div key={i} style={styles.sessionCard}>
                  <p style={styles.sessionDate}>{formatDate(session.date)}</p>
                  <div style={styles.sessionRow}>
                    <div style={styles.sessionStat}>
                      <p style={styles.sessionLabel}>Start page</p>
                      <p style={styles.sessionValue}>{session.startPage}</p>
                    </div>
                    <div style={styles.sessionArrow}>→</div>
                    <div style={styles.sessionStat}>
                      <p style={styles.sessionLabel}>End page</p>
                      <p style={styles.sessionValue}>{session.endPage}</p>
                    </div>
                    <div style={styles.sessionDivider} />
                    <div style={styles.sessionStat}>
                      <p style={styles.sessionLabel}>Pages read</p>
                      <p style={{ ...styles.sessionValue, color: "#4299e1" }}>{session.pagesRead}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ── OUTLINE TAB ── */}
        {activeTab === "scaffolding" && (
          <div>
            {SCAFFOLDING_SECTIONS.map((section) => {
              const sectionNotes = scaffolding[section.id] || {};
              const hasAnyAnswers = section.questions.some(
                (_, i) => (sectionNotes[i] || "").trim() !== ""
              );
              const isOpen = !!expandedOutlineSections[section.id];
              return (
                <div key={section.id} style={styles.scaffoldSection}>
                  <button
                    style={styles.collapsibleHeader}
                    onClick={() => setExpandedOutlineSections((prev) => ({ ...prev, [section.id]: !prev[section.id] }))}
                  >
                    <div style={{ ...styles.scaffoldSectionHeader, marginBottom: 0, borderLeftColor: section.color, flex: 1 }}>
                      <h3 style={{ ...styles.scaffoldSectionTitle, color: section.color }}>
                        {section.title}
                      </h3>
                      {!hasAnyAnswers && (
                        <span style={styles.unansweredBadge}>Not started</span>
                      )}
                    </div>
                    <span style={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                  </button>
                  {isOpen && (
                    <div style={{ marginTop: 16 }}>
                      {section.questions.map((question, i) => {
                        const answer = (sectionNotes[i] || "").trim();
                        return (
                          <div key={i} style={styles.scaffoldQuestion}>
                            <p style={styles.questionText}>{question}</p>
                            {answer ? (
                              <p style={styles.answerText}>{answer}</p>
                            ) : (
                              <p style={styles.noAnswerText}>No answer yet</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── HISTORY TAB ── */}
        {activeTab === "history" && (
          <div>
            {history.length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No outline history yet.</p>
                <p style={styles.emptySubtext}>
                  History is recorded each time your child saves their notes.
                </p>
              </div>
            ) : (
              // Group entries by SCAFFOLDING_SECTIONS canonical order.
              // Sections with no entries are hidden.
              SCAFFOLDING_SECTIONS.map((sectionMeta) => {
                const entries = history.filter((e) => e.section === sectionMeta.id);
                if (entries.length === 0) return null;

                const newCount = entries.filter(
                  (e) => new Date(e.savedAt).getTime() > historyLastViewed
                ).length;
                const isOpen = !!expandedHistorySections[sectionMeta.id];

                return (
                  <div key={sectionMeta.id} style={{ marginBottom: 12 }}>
                    {/* Section header — collapsible trigger */}
                    <button
                      style={{
                        ...styles.collapsibleHeader,
                        backgroundColor: isOpen ? "#f7fafc" : "white",
                        borderRadius: 10,
                        padding: "12px 16px",
                        border: `2px solid ${isOpen ? sectionMeta.color : "#e2e8f0"}`,
                        marginBottom: isOpen ? 8 : 0,
                      }}
                      onClick={() => setExpandedHistorySections((prev) => ({
                        ...prev, [sectionMeta.id]: !prev[sectionMeta.id]
                      }))}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                        <span style={{
                          ...styles.historySection,
                          backgroundColor: sectionMeta.color,
                        }}>
                          {sectionMeta.title}
                        </span>
                        <span style={{ fontSize: 13, color: "#718096" }}>
                          {entries.length} {entries.length === 1 ? "entry" : "entries"}
                        </span>
                        {newCount > 0 && (
                          <span style={styles.historyNewBadge}>{newCount} New</span>
                        )}
                      </div>
                      <span style={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                    </button>

                    {/* Entries — shown when expanded */}
                    {isOpen && entries.map((entry, i) => {
                      const question = sectionMeta.questions[entry.questionIndex];
                      const isNew = new Date(entry.savedAt).getTime() > historyLastViewed;
                      return (
                        <div key={i} style={{
                          ...styles.historyCard,
                          backgroundColor: isNew ? "#fffbeb" : "white",
                          borderLeft: isNew ? "3px solid #f6ad55" : "none",
                        }}>
                          <div style={styles.historyHeader}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{
                                ...styles.historySection,
                                backgroundColor: sectionMeta.color,
                              }}>
                                {sectionMeta.title}
                              </span>
                              {isNew && (
                                <span style={styles.historyNewBadge}>New</span>
                              )}
                            </div>
                            <span style={styles.historyTime}>{formatDateTime(entry.savedAt)}</span>
                          </div>
                          {question && (
                            <p style={styles.historyQuestion}>{question}</p>
                          )}
                          <p style={styles.historyAnswer}>{entry.value}</p>
                        </div>
                      );
                    })}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* ── REPORTS TAB ── */}
        {activeTab === "reports" && (
          <div>
            {/* Existing reports list */}
            {(book.bookReports || []).length === 0 ? (
              <div style={styles.emptyState}>
                <p style={styles.emptyText}>No book reports yet.</p>
                <p style={styles.emptySubtext}>Create a draft below to get started.</p>
              </div>
            ) : (
              [...(book.bookReports || [])].reverse().map((report, reportIndex) => {
                const statusColors = {
                  inProgress: { bg: "#e2e8f0", color: "#4a5568", label: "✏️ In Progress" },
                  submitted: { bg: "#bee3f8", color: "#2b6cb0", label: "📬 Submitted" },
                  kickedBack: { bg: "#fed7d7", color: "#c53030", label: "🔁 Kicked Back" },
                  reviewed: { bg: "#c6f6d5", color: "#276749", label: "✅ Reviewed" },
                };
                const s = statusColors[report.status] || statusColors.inProgress;

                // Count how many fields the child has filled in
                const totalFields = REPORT_FIELDS.length;
                const filledFields = REPORT_FIELDS.filter(
                  (f) => (report.fields?.[f.id]?.value || "").trim() !== ""
                ).length;

                // Default: most recent (first in reversed array, index 0) is open
                const isOpen = Object.keys(expandedReportIds).length === 0
                  ? reportIndex === 0
                  : !!expandedReportIds[report.id];

                return (
                  <div key={report.id} style={styles.quizCard}>
                    {/* Report header — collapsible trigger */}
                    <button
                      style={styles.collapsibleHeader}
                      onClick={() => setExpandedReportIds((prev) => {
                        // On first click, initialize all reports as closed then open this one
                        if (Object.keys(prev).length === 0) {
                          return { [report.id]: true };
                        }
                        return { ...prev, [report.id]: !prev[report.id] };
                      })}
                    >
                      <div style={{ ...styles.quizCardHeader, marginBottom: 0, flex: 1 }}>
                        <div>
                          <p style={styles.quizCardTitle}>{report.title}</p>
                          <p style={styles.quizCardSub}>
                            {filledFields} of {totalFields} fields filled
                          </p>
                        </div>
                        <span style={{
                          ...styles.quizStatusBadge,
                          backgroundColor: s.bg,
                          color: s.color,
                        }}>
                          {s.label}
                        </span>
                      </div>
                      <span style={{ ...styles.chevron, marginLeft: 16 }}>{isOpen ? '▲' : '▼'}</span>
                    </button>

                    {isOpen && (
                      <div style={{ marginTop: 16 }}>
                        {/* Open button — always available */}
                        <button
                          style={styles.editQuizBtn}
                          onClick={() => navigate(`/book/${bookId}/report/${report.id}`)}
                        >
                          ✏️ Open Report
                        </button>

                        {/* Kick Back button — only when submitted */}
                        {report.status === "submitted" && (
                          <button
                            style={{
                              ...styles.editQuizBtn,
                              marginLeft: 8,
                              backgroundColor: "#fed7d7",
                              color: "#c53030",
                              borderColor: "#feb2b2",
                            }}
                            onClick={() => kickBackReport(report.id)}
                          >
                            🔁 Kick Back for Revision
                          </button>
                        )}

                        {/* Per-field feedback — only shown when submitted or kickedBack */}
                        {(report.status === "submitted" || report.status === "kickedBack") && (
                          <div style={{ marginTop: 16, borderTop: "1px solid #e2e8f0", paddingTop: 16 }}>
                            <p style={styles.freeResponseTitle}>Leave Feedback on Fields</p>
                            {REPORT_FIELDS.map((field) => {
                              const fieldData = report.fields?.[field.id] || {};
                              const childAnswer = (fieldData.value || "").trim();
                              if (!childAnswer) return null;
                              const existingFeedback = fieldData.parentFeedback || "";
                              const localFeedback = (reportFeedbackDraft[report.id] || {})[field.id];
                              const feedbackValue = localFeedback !== undefined
                                ? localFeedback : existingFeedback;
                              return (
                                <div key={field.id} style={styles.freeResponseCard}>
                                  <p style={styles.freeResponseQ}>{field.label}</p>
                                  <div style={styles.freeResponseAnswer}>
                                    <p style={styles.freeResponseLabel}>Child's answer:</p>
                                    <p style={styles.freeResponseText}>{childAnswer}</p>
                                  </div>
                                  <textarea
                                    style={{
                                      width: "100%",
                                      padding: 10,
                                      borderRadius: 8,
                                      border: "1px solid #e2e8f0",
                                      fontSize: 14,
                                      boxSizing: "border-box",
                                      minHeight: 70,
                                      resize: "vertical",
                                    }}
                                    placeholder="Leave feedback for this field (optional)..."
                                    value={feedbackValue}
                                    onChange={(e) =>
                                      updateReportFeedbackDraft(report.id, field.id, e.target.value)
                                    }
                                  />
                                </div>
                              );
                            })}
                            {/* Single save button for all feedback */}
                            <button
                              style={styles.saveScoresBtn}
                              onClick={() => saveAllReportFeedback(report.id)}
                              disabled={savingReportFeedback}
                            >
                              {savingReportFeedback ? "Saving..." : "💾 Save All Feedback"}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}

            {/* Create new draft button */}
            <button
              style={styles.addQuizBtn}
              onClick={createNewReport}
              disabled={savingReport}
            >
              {savingReport ? "Creating..." : "+ Create New Draft"}
            </button>
          </div>
        )}

        {/* ── QUIZZES TAB ── */}
        {activeTab === "quizzes" && (
          <div>

            {/* Pending unlocks */}
            {pendingUnlocks.length > 0 && (
              <div style={styles.pendingSection}>
                <h3 style={styles.pendingSectionTitle}>⏳ Waiting for Your Approval</h3>
                {pendingUnlocks.map((quiz) => (
                  <div key={quiz.id} style={styles.pendingCard}>
                    <div style={styles.pendingCardText}>
                      <p style={styles.pendingCardTitle}>{quiz.title}</p>
                      <p style={styles.pendingCardSub}>
                        Your child has reached page {quiz.milestone}. Confirm to unlock this quiz for them.
                      </p>
                    </div>
                    <button style={styles.unlockBtn} onClick={() => confirmUnlock(quiz.id)}>
                      🔓 Unlock
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Existing quizzes */}
            {quizzes.length > 0 && (
              <div style={styles.quizListSection}>
                <h3 style={styles.quizListTitle}>All Quizzes</h3>
                {quizzes.map((quiz, quizIndex) => {
                  const score = calcQuizScore(quiz);
                  // Default: most recent (last in milestone-sorted array) open
                  const isOpen = Object.keys(expandedQuizIds).length === 0
                    ? quizIndex === quizzes.length - 1
                    : !!expandedQuizIds[quiz.id];

                  return (
                    <div key={quiz.id} style={styles.quizCard}>
                      {/* Quiz header — always visible, acts as collapsible trigger */}
                      <button
                        style={styles.collapsibleHeader}
                        onClick={() => setExpandedQuizIds((prev) => {
                          if (Object.keys(prev).length === 0) {
                            // First click: initialize all as closed, open this one
                            const initial = {};
                            quizzes.forEach((q) => { initial[q.id] = false; });
                            return { ...initial, [quiz.id]: true };
                          }
                          return { ...prev, [quiz.id]: !prev[quiz.id] };
                        })}
                      >
                        <div style={{ flex: 1 }}>
                          <p style={{ ...styles.quizCardTitle, margin: "0 0 4px 0" }}>{quiz.title}</p>
                          <p style={{ ...styles.quizCardSub, margin: 0 }}>Milestone: page {quiz.milestone} · {quiz.questions.length} questions</p>
                        </div>
                        <span style={{
                          ...styles.quizStatusBadge,
                          backgroundColor:
                            quiz.needsRevision ? "#fed7d7" :
                            quiz.completedByChild && quiz.reviewedByParent ? "#c6f6d5" :
                            quiz.completedByChild ? "#bee3f8" :
                            quiz.unlockedByParent ? "#bee3f8" :
                            (book.currentPage || 0) >= quiz.milestone ? "#fefcbf" : "#e2e8f0",
                          marginRight: 8,
                        }}>
                          {quiz.needsRevision ? "🔁 Sent Back for Revision" :
                           quiz.completedByChild && quiz.reviewedByParent ? "🏆 Reviewed" :
                           quiz.completedByChild ? "📬 Awaiting Your Review" :
                           quiz.unlockedByParent ? "🔓 Unlocked" :
                           (book.currentPage || 0) >= quiz.milestone ? "⏳ Pending" : "🔒 Upcoming"}
                        </span>
                        <span style={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                      </button>

                      {isOpen && (
                        <div style={{ marginTop: 16 }}>
                          {/* Edit button — only if not yet completed */}
                          {!quiz.completedByChild && (
                            <button
                              style={styles.editQuizBtn}
                              onClick={() => startEditingQuiz(quiz)}
                            >
                              ✏️ Edit Quiz
                            </button>
                          )}

                          {/* Score summary if completed */}
                          {quiz.completedByChild && (
                            <div style={styles.scoreSummary}>
                              <p style={styles.scoreSummaryText}>
                                Auto-scored: {score.earned} / {score.possible} points
                              </p>
                            </div>
                          )}

                          {/* Per-question review + feedback */}
                          {quiz.completedByChild && (
                            <div style={styles.freeResponseSection}>
                              <p style={styles.freeResponseTitle}>Review Answers & Leave Feedback</p>

                              {quiz.questions.map((q, i) => {
                                const childAnswer = (quiz.childAnswers || {})[i];
                                const existingScore = (quiz.parentScores || {})[i];
                                const pendingScore = (pendingScores[quiz.id] || {})[i];
                                const existingFeedback = q.parentFeedback || "";
                                const localFeedback = (quizFeedback[quiz.id] || {})[i];
                                const feedbackValue = localFeedback !== undefined
                                  ? localFeedback : existingFeedback;

                                let answerDisplay = "No answer provided";
                                if (childAnswer !== undefined && childAnswer !== null) {
                                  if (q.type === "true_false") {
                                    answerDisplay = childAnswer === 0 ? "True" : "False";
                                  } else if (q.type === "multiple_choice" || q.type === "dropdown") {
                                    answerDisplay = (q.options || [])[childAnswer] || "—";
                                  } else if (q.type === "multi_select") {
                                    answerDisplay = (childAnswer || [])
                                      .map((ci) => (q.options || [])[ci])
                                      .filter(Boolean)
                                      .join(", ") || "—";
                                  } else {
                                    answerDisplay = childAnswer;
                                  }
                                }

                                let isCorrect = null;
                                if (q.type === "multiple_choice" || q.type === "true_false" || q.type === "dropdown") {
                                  isCorrect = childAnswer === q.correctAnswer;
                                } else if (q.type === "multi_select") {
                                  const correct = new Set(q.correctAnswers || []);
                                  const given = new Set(childAnswer || []);
                                  isCorrect = [...correct].every(c => given.has(c)) &&
                                    [...given].every(g => correct.has(g));
                                }

                                return (
                                  <div key={i} style={styles.freeResponseCard}>
                                    <p style={styles.freeResponseQ}>Q{i + 1}: {q.question}</p>
                                    <div style={{
                                      ...styles.freeResponseAnswer,
                                      borderLeft: isCorrect === true
                                        ? "3px solid #48bb78"
                                        : isCorrect === false
                                          ? "3px solid #f56565"
                                          : "3px solid #e2e8f0",
                                    }}>
                                      <p style={styles.freeResponseLabel}>
                                        Child's answer:
                                        {isCorrect === true && <span style={{ color: "#48bb78", marginLeft: 8 }}>✓ Correct</span>}
                                        {isCorrect === false && <span style={{ color: "#f56565", marginLeft: 8 }}>✗ Incorrect</span>}
                                      </p>
                                      <p style={styles.freeResponseText}>{answerDisplay}</p>
                                    </div>
                                    {q.type === "free_response" && (
                                      <div style={styles.scoreInputRow}>
                                        <label style={styles.scoreInputLabel}>Score (0–10):</label>
                                        <input
                                          style={styles.scoreInput}
                                          type="number"
                                          min="0"
                                          max="10"
                                          placeholder={existingScore !== undefined ? String(existingScore) : "—"}
                                          value={pendingScore !== undefined ? pendingScore : ""}
                                          onChange={(e) => setPendingScore(quiz.id, i, e.target.value)}
                                        />
                                        {existingScore !== undefined && (
                                          <span style={styles.existingScore}>Current: {existingScore}/10</span>
                                        )}
                                      </div>
                                    )}
                                    <textarea
                                      style={{
                                        width: "100%",
                                        padding: 10,
                                        borderRadius: 8,
                                        border: "1px solid #e2e8f0",
                                        fontSize: 14,
                                        boxSizing: "border-box",
                                        minHeight: 60,
                                        resize: "vertical",
                                        marginTop: 10,
                                      }}
                                      placeholder="Leave feedback for this question (optional)..."
                                      value={feedbackValue}
                                      onChange={(e) => updateQuizFeedback(quiz.id, i, e.target.value)}
                                    />
                                  </div>
                                );
                              })}

                              <button
                                style={styles.saveScoresBtn}
                                onClick={() => saveReviewAndFeedback(quiz.id)}
                                disabled={savingReview}
                              >
                                {savingReview ? "Saving..." : "💾 Save Progress"}
                              </button>

                              <div style={styles.reviewActionRow}>
                                <button
                                  style={{ ...styles.reviewActionBtn, backgroundColor: "#48bb78", color: "white" }}
                                  onClick={() => finalizeReview(quiz.id)}
                                  disabled={savingReview}
                                >
                                  ✅ Finalize Review
                                </button>
                                {!quiz.needsRevision && (
                                  <button
                                    style={{ ...styles.reviewActionBtn, backgroundColor: "#fed7d7", color: "#c53030" }}
                                    onClick={() => kickBackQuiz(quiz.id)}
                                    disabled={savingReview}
                                  >
                                    🔁 Send Back for Revision
                                  </button>
                                )}
                              </div>

                              {quiz.reviewedByParent && !quiz.needsRevision && (
                                <p style={styles.reviewedNote}>
                                  ✅ This review is finalized. Your child can see their results and any feedback above.
                                </p>
                              )}
                              {quiz.needsRevision && (
                                <p style={styles.kickedBackNote}>
                                  🔁 This quiz has been sent back to your child for revision. Their original answers and your feedback above are preserved — once they resubmit, their new answers will replace these.
                                </p>
                              )}
                            </div>
                          )}
                          {/* Save as Template — available on any quiz; saves the questions,
                              title, and milestone (not the child's answers or scores) */}
                          <button
                            style={styles.saveAsTemplateBtn}
                            onClick={() => saveAsTemplate(quiz)}
                          >
                            💾 Save as Template
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Template banner — only shown when parent has saved templates for
                this book and the child has no quizzes yet */}
            {showTemplateBanner && !showTemplateApply && (
              <div style={styles.templateBanner}>
                <p style={styles.templateBannerTitle}>
                  📋 You have {matchingTemplates.length} saved quiz{matchingTemplates.length > 1 ? "zes" : ""} for <em>{book.title}</em>
                </p>
                <p style={styles.templateBannerSub}>
                  Apply them to get started quickly — you can edit the title and milestone page before confirming.
                </p>
                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button
                    style={styles.templateApplyBtn}
                    onClick={() => {
                      setTemplateDraft(matchingTemplates.map((t) => ({
                        ...t,
                        selected: true,
                        draftTitle: t.title,
                        draftMilestone: String(t.milestone),
                      })));
                      setShowTemplateApply(true);
                    }}
                  >
                    Apply Quizzes
                  </button>
                  <button style={styles.templateDismissBtn} onClick={dismissTemplates}>
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            {/* Editable apply form — shown when parent clicked "Apply Quizzes" */}
            {showTemplateApply && (
              <div style={styles.templateApplyCard}>
                <h3 style={styles.builderTitle}>Apply Saved Quizzes</h3>
                <p style={{ fontSize: 14, color: "#718096", margin: "0 0 16px 0" }}>
                  Edit titles and milestone pages as needed, then apply.
                </p>
                {templateDraft.map((t, idx) => (
                  <div key={idx} style={styles.templateDraftRow}>
                    <input
                      type="checkbox"
                      checked={t.selected}
                      onChange={() => setTemplateDraft((prev) =>
                        prev.map((item, i) => i === idx ? { ...item, selected: !item.selected } : item)
                      )}
                      style={{ width: 18, height: 18, cursor: "pointer", flexShrink: 0 }}
                    />
                    <input
                      style={{ ...styles.builderInput, flex: 2, marginBottom: 0 }}
                      type="text"
                      placeholder="Quiz title"
                      value={t.draftTitle}
                      onChange={(e) => setTemplateDraft((prev) =>
                        prev.map((item, i) => i === idx ? { ...item, draftTitle: e.target.value } : item)
                      )}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                      <span style={{ fontSize: 13, color: "#718096", whiteSpace: "nowrap" }}>Unlock at page</span>
                      <input
                        style={{ ...styles.builderInput, width: 70, marginBottom: 0, textAlign: "center" }}
                        type="number"
                        min="1"
                        max={book.totalPages}
                        value={t.draftMilestone}
                        onChange={(e) => setTemplateDraft((prev) =>
                          prev.map((item, i) => i === idx ? { ...item, draftMilestone: e.target.value } : item)
                        )}
                      />
                    </div>
                    <span style={{ fontSize: 12, color: "#a0aec0", flexShrink: 0 }}>
                      {t.questions.length} Q
                    </span>
                  </div>
                ))}
                <div style={styles.builderActions}>
                  <button style={styles.cancelBuilderBtn} onClick={() => setShowTemplateApply(false)}>
                    Cancel
                  </button>
                  <button style={styles.saveQuizBtn} onClick={applyTemplates}>
                    ✅ Apply Selected
                  </button>
                </div>
              </div>
            )}

            {/* Saved Templates for this book — always visible when templates exist,
                even after they've been applied to this child's book */}
            {matchingTemplates.length > 0 && (
              <div style={styles.templateManageSection}>
                <h3 style={styles.quizListTitle}>📋 Saved Templates for This Book</h3>
                {matchingTemplates.map((t, idx) => (
                  <div key={idx} style={styles.templateManageCard}>
                    <div style={{ flex: 1 }}>
                      <p style={styles.quizCardTitle}>{t.title}</p>
                      <p style={styles.quizCardSub}>
                        Unlock at page {t.milestone} · {t.questions.length} question{t.questions.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <button
                      style={styles.editQuizBtn}
                      onClick={() => startEditingTemplate(idx)}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      style={{ ...styles.editQuizBtn, backgroundColor: "#fff5f5", color: "#c53030", borderColor: "#feb2b2", marginLeft: 8 }}
                      onClick={() => deleteTemplate(idx)}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Quiz builder */}
            <div style={styles.builderSection}>
              {!showQuizBuilder ? (
                <button style={styles.addQuizBtn} onClick={() => setShowQuizBuilder(true)}>
                  + Create New Quiz
                </button>
              ) : (
                <div style={styles.builderCard} ref={builderRef}>
                  <h3 style={styles.builderTitle}>
                    {editingTemplateIdx !== null ? "Edit Template" : editingQuizId ? "Edit Quiz" : "Create a New Quiz"}
                  </h3>

                  <label style={styles.builderLabel}>Quiz Title</label>
                  <input
                    style={styles.builderInput}
                    type="text"
                    placeholder="e.g. Chapters 1–5 Quiz"
                    value={quizTitle}
                    onChange={(e) => setQuizTitle(e.target.value)}
                  />

                  <label style={styles.builderLabel}>Unlock at Page</label>
                  <input
                    style={styles.builderInput}
                    type="number"
                    placeholder={`1 – ${book.totalPages}`}
                    value={quizMilestone}
                    onChange={(e) => setQuizMilestone(e.target.value)}
                  />

                  {/* Questions */}
                  {quizQuestions.map((q, qIndex) => (
                    <div key={qIndex} style={styles.questionBuilder}>
                      <div style={styles.questionBuilderHeader}>
                        <span style={styles.questionBuilderLabel}>
                          Q{qIndex + 1} · {QUESTION_TYPES.find(t => t.type === q.type)?.label}
                        </span>
                        <button style={styles.removeQBtn} onClick={() => removeQuestion(qIndex)}>✕ Remove</button>
                      </div>

                      <input
                        style={styles.builderInput}
                        type="text"
                        placeholder="Enter your question..."
                        value={q.question}
                        onChange={(e) => updateQuestion(qIndex, "question", e.target.value)}
                      />

                      {/* True/False */}
                      {q.type === "true_false" && (
                        <div style={styles.tfBuilderRow}>
                          {["True", "False"].map((label, oi) => (
                            <button
                              key={oi}
                              style={{
                                ...styles.tfBuilderBtn,
                                backgroundColor: q.correctAnswer === oi ? "#c6f6d5" : "#f7fafc",
                                borderColor: q.correctAnswer === oi ? "#48bb78" : "#e2e8f0",
                              }}
                              onClick={() => toggleCorrectAnswer(qIndex, oi)}
                            >
                              {label} {q.correctAnswer === oi ? "✓" : ""}
                            </button>
                          ))}
                          <p style={styles.tfHint}>Tap to set correct answer</p>
                        </div>
                      )}

                      {/* Options-based types */}
                      {(q.type === "multiple_choice" || q.type === "multi_select" || q.type === "dropdown") && (
                        <div>
                          <p style={styles.optionsHint}>
                            {q.type === "multi_select"
                              ? "Tap options to mark correct answers (can be multiple)"
                              : "Tap an option to mark it as correct"}
                          </p>
                          {(q.options || []).map((opt, oi) => (
                            <div key={oi} style={styles.optionBuilderRow}>
                              <input
                                style={styles.optionBuilderInput}
                                type="text"
                                placeholder={`Option ${oi + 1}`}
                                value={opt}
                                onChange={(e) => updateOption(qIndex, oi, e.target.value)}
                              />
                              <button
                                style={{
                                  ...styles.correctToggle,
                                  backgroundColor:
                                    q.type === "multi_select"
                                      ? (q.correctAnswers || []).includes(oi) ? "#c6f6d5" : "#f7fafc"
                                      : q.correctAnswer === oi ? "#c6f6d5" : "#f7fafc",
                                  borderColor:
                                    q.type === "multi_select"
                                      ? (q.correctAnswers || []).includes(oi) ? "#48bb78" : "#e2e8f0"
                                      : q.correctAnswer === oi ? "#48bb78" : "#e2e8f0",
                                }}
                                onClick={() => toggleCorrectAnswer(qIndex, oi)}
                              >
                                ✓
                              </button>
                              {(q.options || []).length > 2 && (
                                <button style={styles.removeOptBtn} onClick={() => removeOption(qIndex, oi)}>✕</button>
                              )}
                            </div>
                          ))}
                          <button style={styles.addOptionBtn} onClick={() => addOption(qIndex)}>
                            + Add Option
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add question type buttons */}
                  <p style={styles.addQuestionLabel}>Add a Question</p>
                  <div style={styles.questionTypeRow}>
                    {QUESTION_TYPES.map((t) => (
                      <button key={t.type} style={styles.typeBtn} onClick={() => addQuestion(t.type)}>
                        + {t.label}
                      </button>
                    ))}
                  </div>

                  <div style={styles.builderActions}>
                    <button style={styles.cancelBuilderBtn} onClick={() => {
                      setShowQuizBuilder(false);
                      setQuizTitle("");
                      setQuizMilestone("");
                      setQuizQuestions([]);
                      setEditingQuizId(null);
                      setEditingTemplateIdx(null);
                    }}>
                      Cancel
                    </button>
                    <button style={styles.saveQuizBtn} onClick={saveQuiz} disabled={savingQuiz}>
                      {savingQuiz ? "Saving..." : "💾 Save Quiz"}
                    </button>
                  </div>
                </div>
              )}
            </div>

          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: "100vh", backgroundColor: "#f0f4f8" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" },

  modalOverlay: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modalBox: { backgroundColor: "white", borderRadius: 16, padding: 28, maxWidth: 360, width: "90%", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", position: "relative", zIndex: 1001 },
  modalTitle: { fontSize: 20, color: "#2d3748", margin: "0 0 12px 0" },
  modalText: { fontSize: 15, color: "#718096", margin: "0 0 24px 0", lineHeight: 1.6 },
  modalButtons: { display: "flex", gap: 10 },
  modalCancel: { flex: 1, padding: 12, backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
  modalConfirm: { flex: 1, padding: 12, backgroundColor: "#f56565", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },

  pageHeader: { backgroundColor: "white", padding: "16px 24px", display: "flex", alignItems: "center", gap: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.05)" },
  backBtn: { padding: "8px 14px", backgroundColor: "#e2e8f0", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" },
  headerText: { flex: 1 },
  title: { fontSize: 18, color: "#2d3748", margin: "0 0 2px 0" },
  subtitle: { fontSize: 13, color: "#718096", margin: 0 },
  headerCover: { width: 44, height: 66, objectFit: "cover", borderRadius: 4 },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#a0aec0", fontWeight: "bold", lineHeight: 1, alignSelf: "flex-start", padding: "0 0 0 4px" },

  summaryBar: { backgroundColor: "white", padding: "12px 24px", borderBottom: "1px solid #e2e8f0" },
  progressBar: { height: 8, backgroundColor: "#e2e8f0", borderRadius: 4, marginBottom: 6 },
  progressFill: { height: "100%", backgroundColor: "#4299e1", borderRadius: 4 },
  summaryFooter: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  progressText: { fontSize: 13, color: "#718096", margin: 0 },
  editBookBtn: { fontSize: 12, color: "#4a5568", backgroundColor: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontWeight: "bold" },

  unlockBanner: { backgroundColor: "#fefcbf", borderBottom: "1px solid #f6e05e", padding: "10px 24px" },
  unlockBannerText: { fontSize: 14, color: "#744210", margin: 0, fontWeight: "bold" },

  tabRow: { display: "flex", backgroundColor: "white", borderBottom: "1px solid #e2e8f0" },
  tabActive: { flex: 1, padding: "14px 6px", backgroundColor: "white", border: "none", borderBottom: "3px solid #805ad5", color: "#805ad5", fontWeight: "bold", fontSize: 13, cursor: "pointer" },
  tabInactive: { flex: 1, padding: "14px 6px", backgroundColor: "white", border: "none", borderBottom: "3px solid transparent", color: "#718096", fontSize: 13, cursor: "pointer" },

  // Collapsible section styles (Phase 4)
  collapsibleHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", background: "none", border: "none", width: "100%", padding: 0, textAlign: "left" },
  chevron: { fontSize: 14, color: "#a0aec0", marginLeft: 8, flexShrink: 0 },
  historyNewBadge: { fontSize: 11, fontWeight: "bold", color: "white", backgroundColor: "#e53e3e", borderRadius: 8, padding: "2px 7px" },

  content: { padding: 20, maxWidth: 700, margin: "0 auto" },

  emptyState: { textAlign: "center", padding: "60px 24px" },
  emptyText: { fontSize: 17, color: "#4a5568", margin: "0 0 8px 0" },
  emptySubtext: { fontSize: 14, color: "#a0aec0", margin: 0 },

  sessionCard: { backgroundColor: "white", borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" },
  sessionDate: { fontSize: 13, color: "#718096", margin: "0 0 12px 0", fontWeight: "bold" },
  sessionRow: { display: "flex", alignItems: "center", gap: 12 },
  sessionStat: { textAlign: "center" },
  sessionLabel: { fontSize: 11, color: "#a0aec0", margin: "0 0 4px 0", textTransform: "uppercase", letterSpacing: "0.05em" },
  sessionValue: { fontSize: 22, fontWeight: "bold", color: "#2d3748", margin: 0 },
  sessionArrow: { fontSize: 18, color: "#a0aec0", flex: 1, textAlign: "center" },
  sessionDivider: { width: 1, height: 40, backgroundColor: "#e2e8f0", margin: "0 4px" },

  scaffoldSection: { backgroundColor: "white", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" },
  scaffoldSectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", borderLeft: "4px solid", paddingLeft: 12, marginBottom: 16 },
  scaffoldSectionTitle: { fontSize: 17, fontWeight: "bold", margin: 0 },
  unansweredBadge: { fontSize: 12, color: "#a0aec0", backgroundColor: "#f7fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "2px 10px" },
  scaffoldQuestion: { marginBottom: 16, paddingBottom: 16, borderBottom: "1px solid #f0f4f8" },
  questionText: { fontSize: 13, fontWeight: "bold", color: "#4a5568", margin: "0 0 6px 0" },
  answerText: { fontSize: 15, color: "#2d3748", margin: 0, lineHeight: 1.6 },
  noAnswerText: { fontSize: 14, color: "#cbd5e0", margin: 0, fontStyle: "italic" },

  historyCard: { backgroundColor: "white", borderRadius: 12, padding: 20, marginBottom: 12, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" },
  historyHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  historySection: { fontSize: 12, color: "white", fontWeight: "bold", borderRadius: 12, padding: "3px 10px" },
  historyTime: { fontSize: 12, color: "#a0aec0" },
  historyQuestion: { fontSize: 13, color: "#718096", margin: "0 0 6px 0", fontStyle: "italic" },
  historyAnswer: { fontSize: 15, color: "#2d3748", margin: 0, lineHeight: 1.6 },

  // Quizzes tab
  pendingSection: { marginBottom: 24 },
  pendingSectionTitle: { fontSize: 16, color: "#744210", margin: "0 0 12px 0" },
  pendingCard: { display: "flex", alignItems: "center", gap: 12, backgroundColor: "#fefcbf", borderRadius: 10, padding: "14px 16px", marginBottom: 10, border: "1px solid #f6e05e" },
  pendingCardText: { flex: 1 },
  pendingCardTitle: { fontSize: 15, fontWeight: "bold", color: "#2d3748", margin: "0 0 4px 0" },
  pendingCardSub: { fontSize: 13, color: "#718096", margin: 0 },
  unlockBtn: { padding: "10px 16px", backgroundColor: "#48bb78", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold", whiteSpace: "nowrap" },

  quizListSection: { marginBottom: 24 },
  quizListTitle: { fontSize: 16, color: "#2d3748", margin: "0 0 12px 0" },
  quizCard: { backgroundColor: "white", borderRadius: 12, padding: 20, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.06)" },
  quizCardHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 },
  quizCardTitle: { fontSize: 16, fontWeight: "bold", color: "#2d3748", margin: "0 0 4px 0" },
  quizCardSub: { fontSize: 13, color: "#718096", margin: 0 },
  quizStatusBadge: { fontSize: 12, fontWeight: "bold", borderRadius: 12, padding: "4px 10px", whiteSpace: "nowrap" },
  scoreSummary: { backgroundColor: "#f7fafc", borderRadius: 8, padding: "10px 14px", marginBottom: 12 },
  scoreSummaryText: { fontSize: 14, color: "#2d3748", margin: 0, fontWeight: "bold" },

  freeResponseSection: { borderTop: "1px solid #e2e8f0", paddingTop: 16, marginTop: 8 },
  freeResponseTitle: { fontSize: 14, fontWeight: "bold", color: "#4a5568", margin: "0 0 12px 0" },
  freeResponseCard: { backgroundColor: "#f7fafc", borderRadius: 8, padding: 14, marginBottom: 12 },
  freeResponseQ: { fontSize: 14, fontWeight: "bold", color: "#2d3748", margin: "0 0 8px 0" },
  freeResponseAnswer: { backgroundColor: "white", borderRadius: 6, padding: 10, marginBottom: 10 },
  freeResponseLabel: { fontSize: 11, color: "#a0aec0", margin: "0 0 4px 0", textTransform: "uppercase", fontWeight: "bold" },
  freeResponseText: { fontSize: 14, color: "#2d3748", margin: 0, lineHeight: 1.6 },
  scoreInputRow: { display: "flex", alignItems: "center", gap: 10 },
  scoreInputLabel: { fontSize: 13, color: "#4a5568", fontWeight: "bold" },
  scoreInput: { width: 70, padding: "6px 10px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 15, textAlign: "center" },
  existingScore: { fontSize: 13, color: "#48bb78", fontWeight: "bold" },
  saveScoresBtn: { width: "100%", padding: 12, backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold", marginTop: 8 },
  reviewActionRow: { display: "flex", gap: 8, marginTop: 8 },
  reviewActionBtn: { flex: 1, padding: 12, border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold" },
  reviewedNote: { fontSize: 13, color: "#276749", backgroundColor: "#f0fff4", border: "1px solid #c6f6d5", borderRadius: 8, padding: "10px 14px", marginTop: 10, lineHeight: 1.5 },
  kickedBackNote: { fontSize: 13, color: "#c53030", backgroundColor: "#fff5f5", border: "1px solid #fed7d7", borderRadius: 8, padding: "10px 14px", marginTop: 10, lineHeight: 1.5 },

  builderSection: { marginTop: 8 },
  addQuizBtn: { width: "100%", padding: 14, backgroundColor: "#805ad5", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
  saveAsTemplateBtn: { width: "100%", padding: 10, backgroundColor: "#f7fafc", color: "#4a5568", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: "bold", marginTop: 10 },
  templateBanner: { backgroundColor: "#ebf8ff", border: "2px solid #4299e1", borderRadius: 12, padding: "16px 20px", marginBottom: 16 },
  templateBannerTitle: { fontSize: 15, fontWeight: "bold", color: "#2b6cb0", margin: "0 0 6px 0" },
  templateBannerSub: { fontSize: 13, color: "#4a5568", margin: 0 },
  templateApplyBtn: { padding: "10px 20px", backgroundColor: "#4299e1", color: "white", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer", fontWeight: "bold" },
  templateDismissBtn: { padding: "10px 16px", backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" },
  templateApplyCard: { backgroundColor: "white", borderRadius: 12, padding: 24, marginBottom: 16, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" },
  templateDraftRow: { display: "flex", alignItems: "center", gap: 10, marginBottom: 12 },
  templateManageSection: { marginBottom: 16 },
  templateManageCard: { backgroundColor: "white", borderRadius: 10, padding: "14px 16px", marginBottom: 10, boxShadow: "0 2px 4px rgba(0,0,0,0.06)", display: "flex", alignItems: "center", gap: 12 },
  builderCard: { backgroundColor: "white", borderRadius: 12, padding: 24, boxShadow: "0 2px 4px rgba(0,0,0,0.08)" },
  builderTitle: { fontSize: 18, color: "#2d3748", margin: "0 0 16px 0" },
  builderLabel: { display: "block", fontSize: 13, fontWeight: "bold", color: "#4a5568", margin: "0 0 6px 0" },
  builderInput: { width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 15, boxSizing: "border-box", marginBottom: 16 },

  questionBuilder: { backgroundColor: "#f7fafc", borderRadius: 10, padding: 16, marginBottom: 16 },
  questionBuilderHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  questionBuilderLabel: { fontSize: 13, fontWeight: "bold", color: "#805ad5" },
  removeQBtn: { fontSize: 12, color: "#f56565", background: "none", border: "none", cursor: "pointer", fontWeight: "bold" },

  tfBuilderRow: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" },
  tfBuilderBtn: { padding: "10px 20px", borderRadius: 8, border: "2px solid", cursor: "pointer", fontSize: 14, fontWeight: "bold" },
  tfHint: { fontSize: 12, color: "#a0aec0", margin: 0 },

  optionsHint: { fontSize: 12, color: "#718096", margin: "0 0 8px 0", fontStyle: "italic" },
  optionBuilderRow: { display: "flex", gap: 8, alignItems: "center", marginBottom: 8 },
  optionBuilderInput: { flex: 1, padding: "8px 10px", borderRadius: 6, border: "1px solid #e2e8f0", fontSize: 14 },
  correctToggle: { padding: "8px 12px", borderRadius: 6, border: "2px solid", cursor: "pointer", fontSize: 14, fontWeight: "bold" },
  removeOptBtn: { padding: "6px 10px", backgroundColor: "#fed7d7", color: "#c53030", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 13 },
  addOptionBtn: { fontSize: 13, color: "#4299e1", background: "none", border: "none", cursor: "pointer", fontWeight: "bold", padding: "4px 0", marginBottom: 8 },

  addQuestionLabel: { fontSize: 13, fontWeight: "bold", color: "#4a5568", margin: "16px 0 8px 0" },
  questionTypeRow: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 },
  typeBtn: { padding: "8px 14px", backgroundColor: "#ebf8ff", color: "#2b6cb0", border: "1px solid #bee3f8", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: "bold" },

  builderActions: { display: "flex", gap: 10 },
  cancelBuilderBtn: { flex: 1, padding: 12, backgroundColor: "#e2e8f0", color: "#4a5568", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer" },
  saveQuizBtn: { flex: 2, padding: 12, backgroundColor: "#805ad5", color: "white", border: "none", borderRadius: 8, fontSize: 15, cursor: "pointer", fontWeight: "bold" },
  editQuizBtn: { padding: "6px 14px", backgroundColor: "#ebf8ff", color: "#2b6cb0", border: "1px solid #bee3f8", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: "bold", marginBottom: 12 },
};